"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";

interface PaymentMethod {
  id: string;
  type: "card" | "bank";
  name: string;
  lastDigits: string;
  expiryDate?: string;
  icon: string;
  isDefault: boolean;
}

const MakePaymentScreen: React.FC = () => {
  const [amount, setAmount] = useState("1200.00");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card1");
  
  const paymentMethods: PaymentMethod[] = [
    {
      id: "card1",
      type: "card",
      name: "Visa",
      lastDigits: "1234",
      expiryDate: "09/28",
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/45e810e4d78cfdbd39c7a61e2e3a5d7731b9d40f?placeholderIfAbsent=true",
      isDefault: true
    },
    {
      id: "card2",
      type: "card",
      name: "Mastercard",
      lastDigits: "5678",
      expiryDate: "11/27",
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/3b094f47bcdd7c5cafa94cc1d0e8ba46ded5b718?placeholderIfAbsent=true",
      isDefault: false
    },
    {
      id: "bank1",
      type: "bank",
      name: "Direct Debit",
      lastDigits: "9012",
      icon: "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/c6f51e4df4cd9c95f9b66c3c2067cc5debb56b47?placeholderIfAbsent=true",
      isDefault: false
    }
  ];
  
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-digit characters except decimal point
    const value = e.target.value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      return;
    }
    // Limit to 2 decimal places
    if (parts.length > 1 && parts[1].length > 2) {
      return;
    }
    setAmount(value);
  };
  
  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-8 pb-12 w-full">
          <header className="w-full text-2xl font-semibold leading-none text-gray-900">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="flex flex-col justify-center w-full">
                  <h1 className="w-full">
                    Make Payment
                  </h1>
                </div>
              </div>
            </div>
          </header>

          <section className="mt-8 w-full">
            <div className="px-4 w-full">
              {/* Amount Input */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Amount (ZAR)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="text"
                      value={amount}
                      onChange={handleAmountChange}
                      className="w-full border border-gray-300 rounded-lg py-3 px-10 text-right font-semibold text-gray-900 text-lg focus:ring focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
                    />
                  </div>
                </div>
                
                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex justify-between">
                    <button className="py-2 px-4 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">R500</button>
                    <button className="py-2 px-4 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">R1000</button>
                    <button className="py-2 px-4 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">R1500</button>
                  </div>
                  <div className="flex justify-between">
                    <button className="py-2 px-4 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">R2000</button>
                    <button className="py-2 px-4 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">R2500</button>
                    <button className="py-2 px-4 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">R3000</button>
                  </div>
                </div>
              </div>
              
              {/* Payment Methods */}
              <div className="mb-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Payment Method</h2>
                
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-200">
                  {paymentMethods.map(method => (
                    <div 
                      key={method.id}
                      className={`p-4 flex items-center ${method.isDefault ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.id}
                        checked={selectedPaymentMethod === method.id}
                        onChange={() => setSelectedPaymentMethod(method.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      
                      <div className="flex items-center gap-3 ml-3 flex-1">
                        <img src={method.icon} alt={method.name} className="w-10 h-10 object-contain" />
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            {method.type === 'card' ? `${method.name} •••• ${method.lastDigits}` : method.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {method.type === 'card' 
                              ? `Expires ${method.expiryDate}`
                              : `Account ending in ${method.lastDigits}`
                            }
                          </p>
                        </div>
                      </div>
                      
                      {method.isDefault && (
                        <span className="text-xs font-medium text-blue-600 py-1 px-2 bg-blue-100 rounded-full">Default</span>
                      )}
                    </div>
                  ))}
                </div>
                
                <button className="mt-4 flex items-center gap-2 text-sm font-medium text-blue-600">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add New Payment Method
                </button>
              </div>
              
              {/* Payment Details */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Payment Details</h2>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount</span>
                    <span className="font-medium text-gray-900">R{amount}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Service Fee</span>
                    <span className="font-medium text-gray-900">R0.00</span>
                  </div>
                  
                  <div className="h-px bg-gray-200 my-2"></div>
                  
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-gray-900">Total</span>
                    <span className="text-gray-900">R{amount}</span>
                  </div>
                </div>
              </div>
              
              {/* Complete Payment Button */}
              <button className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 transition-colors">
                Complete Payment
              </button>
              
              <p className="mt-4 text-xs text-center text-gray-500">
                By proceeding, you agree to our <span className="text-blue-600">Terms of Service</span> and <span className="text-blue-600">Privacy Policy</span>
              </p>
            </div>
          </section>
        </section>
      </div>

      <TabBar />
    </main>
  );
};

export default MakePaymentScreen;
